import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AgtFitnessVm } from 'app/stores/ui/agt.fitness.ui.store';
import { useLocation } from 'react-router';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import routes from 'routes';
import { FitnessTrackerGraphics } from './FitnessTrackerGraphics';
import { LeadNowAbos } from 'app/stores/ui/agt.leadnow.ui.store';
import { EChartsOption, ECharts, init } from 'echarts';
import _, { debounce } from 'lodash';
import colors from 'assets/scss/colors.module.scss';
import Widget from 'app/components/common/Widget';
import { LeadNowHistoryChart } from './FitnessTrackerLeadNowHistory';

export const FitnessTrackerLeadNow = observer(() => {
	const { berichteUiiStore, agtFitnessUiStore, agenturListUiStore } = useStore();
	const [hasItems, setHasItems] = useState(false);
	const [filteredAgtIds, setFilteredAgtIds] = useState<number[]>([]);

	const [selectedAgtId, setSelectedAgtId] = useState<number | undefined>(undefined);
	const history = useHistoryWithQuery();
	const location = useLocation();

	const tm = useMemo(() => {
		const tm = new TableModel<AgtFitnessVm, number>();
		const cols: ITableColumn<AgtFitnessVm, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		cols.push({
			label: 'Brutto Leads',
			path: 'leadNowBrutto',
			cellModifier: 'border-left-turquoise bg-turquoise',
			labelModifier: 'border-left-turquoise bg-turquoise',
			agg: {
				path: 'leadNowBrutto',
				aggType: AggregrationType.sum,
			},
		});
		cols.push({
			label: 'Stornierungen',
			path: 'leadNowStorno',
			cellModifier: 'bg-turquoise',
			labelModifier: 'bg-turquoise',
			agg: {
				path: 'leadNowStorno',
				aggType: AggregrationType.sum,
			},
		});
		cols.push({
			label: 'Netto Leads',
			path: 'leadNowNetto',
			cellModifier: 'border-left-turquoise bg-turquoise',
			labelModifier: 'border-left-turquoise bg-turquoise',
			agg: {
				path: 'leadNowNetto',
				aggType: AggregrationType.sum,
			},
		});

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.onRowClick = (row) => {
			const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'leadnow', row.data.agt.agtId.toString());
			history.pushWithQuery(p);
		};

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		agtFitnessUiStore.load().then(() => {
			let items = agtFitnessUiStore.items;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as AgtFitnessVm[];
			}
			setHasItems(items.length > 0);
			// Store all agency IDs for aggregated chart view
			if (items.length > 0) {
				const agtIds = items.map((item) => item.agt.agtId);
				setFilteredAgtIds(agtIds);
			} else {
				setFilteredAgtIds([]);
			}
			tm.setRowData(items);
			tm.sort();
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtFitnessUiStore.items, agtFitnessUiStore]);

	useEffect(() => {
		// das hier ginge auch nicer, via treeNode Model und openByPath
		const lastPathElement = location.pathname.split('/').pop();
		let agtId;
		if (typeof lastPathElement === 'string') {
			agtId = parseInt(lastPathElement);
		}
		if (agtId && !isNaN(agtId)) {
			setSelectedAgtId(agtId);
		} else {
			setSelectedAgtId(undefined);
		}
	}, [location]);

	const onClose = () => {
		const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'leadnow');
		history.pushWithQuery(p);
	};

	return (
		<>
			<div className="table-container">
				{!hasItems ? (
					<div className="pad-1rem">Keine Fitnesstrackerdaten gefunden</div>
				) : (
					<>
						{!selectedAgtId && filteredAgtIds.length > 0 && (
							<div className="pad-1rem pad-bottom-2rem is-sticky">
								<LeadNowHistoryChart agtIds={filteredAgtIds} />
							</div>
						)}
						{selectedAgtId && <FitnessTrackerGraphics agtId={selectedAgtId} view="leadnow" onClose={onClose} />}
						<Table stickyHeader={true} stickyFooter={true} tm={tm}></Table>
					</>
				)}
			</div>
		</>
	);
});

interface IFitnessTrackerLeadNowChart {
	fitVm: AgtFitnessVm;
}
export const FitnessTrackerLeadNowBruttoNettoChart = observer((props: IFitnessTrackerLeadNowChart) => {
	const { fitVm } = props;
	if (!fitVm.leadNow) return <></>;

	// echarts stuff
	const chartRef = useRef<HTMLDivElement>(null);
	const sourceData = [
		{
			label: 'Brutto',
			val: fitVm.leadNow ? fitVm.leadNow.brutto : 0,
		},
		{
			label: 'Netto',
			val: fitVm.leadNow ? fitVm.leadNow.netto : 0,
		},
	];

	useEffect(() => {
		const dim = ['label', 'val'];
		const chartHeight = 500;

		let option: EChartsOption = {
			color: colors.turquoise,
			dataset: {
				dimensions: dim,
				source: sourceData,
			},
			xAxis: {
				type: 'category',
				axisLabel: {
					interval: 0,
					fontSize: 14,
					padding: [0, 5, 0, 0],
				},
			},
			yAxis: {
				type: 'value',
			},
			series: {
				type: 'bar',
				itemStyle: {
					color: (params) => {
						return params.dataIndex === 0 ? colors.richTurquoise : colors.turquoise;
					},
				},
				encode: {
					x: 'label',
					y: 'val',
				},
			},
			legend: {
				show: true,
			},
			tooltip: {
				trigger: 'item',
			},
			grid: {
				containLabel: true,
			},
		};

		let chart: ECharts | null = null;
		if (chartRef.current !== null) {
			chart = init(chartRef.current, null, { renderer: 'svg', height: chartHeight });
			chart.setOption(option);
		}
		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);
		window.addEventListener('resize', handleResize);

		return () => {
			chart?.dispose();
			window.removeEventListener('resize', handleResize.cancel);
		};
	}, [chartRef, sourceData]);

	const action = <span className={`tag is-turquoise`}>AMIS.NOW Leads</span>;
	return (
		<Widget title={'Lead-Zulieferungen'} disableCollapse={true} action={action} modifier="is-paddingless">
			<div style={{ width: '100%', height: 'auto', background: '#ffffff' }} ref={chartRef}></div>
		</Widget>
	);
});
export const FitnessTrackerLeadNowChart = observer((props: IFitnessTrackerLeadNowChart) => {
	const { fitVm } = props;

	const excludes = ['amisNow.item.summe', 'amisNow.item.davonRead', 'amisNow.item.quoteRead'];
	const columns = LeadNowAbos.filter((p) => excludes.indexOf(p.path) === -1);

	if (!fitVm.leadNow) return <></>;

	// echarts stuff
	const chartRef = useRef<HTMLDivElement>(null);
	const sourceData = columns.map((c) => ({
		label: c.label,
		val: _.get(fitVm.leadNow, c.path).brutto,
	}));

	useEffect(() => {
		const dim = ['label', 'val'];
		const chartHeight = 500;

		let option: EChartsOption = {
			color: colors.turquoise,
			dataset: {
				dimensions: dim,
				source: sourceData,
			},
			yAxis: {
				type: 'category',
				axisLabel: {
					interval: 0,
					fontSize: 14,
					padding: [0, 5, 0, 0],
				},
			},
			xAxis: {
				type: 'value',
			},
			series: {
				type: 'bar',
				encode: {
					y: 'label',
					x: 'val',
				},
			},
			legend: {
				show: true,
			},
			tooltip: {
				trigger: 'item',
			},
			grid: {
				containLabel: true,
			},
		};

		let chart: ECharts | null = null;
		if (chartRef.current !== null) {
			chart = init(chartRef.current, null, { renderer: 'svg', height: chartHeight });
			chart.setOption(option);
		}
		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);
		window.addEventListener('resize', handleResize);

		return () => {
			chart?.dispose();
			window.removeEventListener('resize', handleResize.cancel);
		};
	}, [chartRef, sourceData]);

	return (
		<>
			<Widget title={'Aufteilung nach Produkt-Abos'} disableCollapse={true} modifier="is-paddingless">
				<div style={{ width: '100%', height: 'auto', background: '#ffffff' }} ref={chartRef}></div>
			</Widget>
		</>
	);
});
